import { BaseNumber, CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions } from '@tanstack/react-query'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'

import {
  cookie3AirdropRewardsAddress,
  ignitionAirdropRewardsAddress,
  socialAndPreFarmingAirdropRewardsAddress,
} from '@/config/abis/addresses'
import { sparkRewardsAbi } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { Token } from '@/domain/types/Token'

export interface AirdropClaimsQueryOptionsParams {
  address: CheckedAddress | undefined
  chainId: number
  wagmiConfig: Config
  spk: Token
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function airdropClaimsQueryOptions({ address, chainId, wagmiConfig, spk }: AirdropClaimsQueryOptionsParams) {
  return queryOptions({
    queryKey: airdropClaimsQueryKey({ address, chainId }),
    queryFn: () => airdropClaimsQueryFunction({ address, chainId, wagmiConfig, spk }),
  })
}

export function airdropClaimsQueryKey({
  address,
  chainId,
}: Pick<AirdropClaimsQueryOptionsParams, 'address' | 'chainId'>): QueryKey {
  return ['spk-airdrop-claims', address, chainId]
}

export type AirdropClaimsQueryData = {
  ignition: NormalizedNumber
  preFarmingAndSocial: NormalizedNumber
  cookie3: NormalizedNumber
  overdrive: NormalizedNumber
  total: NormalizedNumber
} | null

export async function airdropClaimsQueryFunction({
  address,
  wagmiConfig,
  chainId,
  spk,
}: AirdropClaimsQueryOptionsParams): Promise<AirdropClaimsQueryData> {
  if (!address) {
    return null
  }

  const [ignitionClaimed, preFarmingAndSocialClaimed, cookie3Claimed, overdriveClaimed] = await Promise.all([
    readContract(wagmiConfig, {
      address: getContractAddress(ignitionAirdropRewardsAddress, chainId),
      abi: sparkRewardsAbi,
      functionName: 'cumulativeClaimed',
      args: [address, spk.address, 1n],
      chainId,
    }),
    readContract(wagmiConfig, {
      address: getContractAddress(socialAndPreFarmingAirdropRewardsAddress, chainId),
      abi: sparkRewardsAbi,
      functionName: 'cumulativeClaimed',
      args: [address, spk.address, 1n],
      chainId,
    }),
    readContract(wagmiConfig, {
      address: getContractAddress(cookie3AirdropRewardsAddress, chainId),
      abi: sparkRewardsAbi,
      functionName: 'cumulativeClaimed',
      args: [address, spk.address, 1n],
      chainId,
    }),
    readContract(wagmiConfig, {
      address: getContractAddress(ignitionAirdropRewardsAddress, chainId),
      abi: sparkRewardsAbi,
      functionName: 'cumulativeClaimed',
      args: [address, spk.address, 2n],
      chainId,
    }),
  ])

  return {
    ignition: BaseNumber(ignitionClaimed).toNormalizedUnit(spk.decimals),
    preFarmingAndSocial: BaseNumber(preFarmingAndSocialClaimed).toNormalizedUnit(spk.decimals),
    cookie3: BaseNumber(cookie3Claimed).toNormalizedUnit(spk.decimals),
    overdrive: BaseNumber(overdriveClaimed).toNormalizedUnit(spk.decimals),
    total: BaseNumber(
      ignitionClaimed + preFarmingAndSocialClaimed + cookie3Claimed + overdriveClaimed,
    ).toNormalizedUnit(spk.decimals),
  }
}
